import {
  setTemplate,
  setPalette,
  setStrokeWidth,
  setFont,
  setTextTransform,
  setGenerations,
  setThematic,
  setAngle,
  setAngleOffset,
  setMargin,
  setChartBackground,
  setHeading,
  setSubheading,
} from '../chart';
import { setThickness, setFontSize, setOffset, setCellType, setStrokeVisible } from '../generations';
import { setName } from '../cells';
import { setGenerationsLocked } from '../ui';
import * as palettes from '../palettes/options';
import { find, reverse, range } from 'lodash';

export function initProductTemplate(dispatch, state, query) {
  const productTemplate = productTemplates[query.productHandle];

  if (!productTemplate) {
    return;
  }

  dispatch(setTemplate(query.template || productTemplate.template));

  const generations = productTemplate.generations || Math.max(4, Number.parseInt(query.generations) || 5);
  const palette = palettes[productTemplate.palette];
  const variation = find(palette.variations, variation => variation.name === productTemplate.variation);
  const colors = palette.getColors(variation, generations);
  const bandColors = palette.getBandColors ? palette.getBandColors(variation) : [];

  dispatch(setPalette({
    ...variation,
    colors: productTemplate.flipped ? reverse(colors) : colors,
    bandColors: productTemplate.flipped ? reverse(bandColors) : bandColors,
  }));

  if (productTemplate.strokeWidth !== undefined) {
    dispatch(setStrokeWidth(productTemplate.strokeWidth));
  }

  if (productTemplate.font) {
    dispatch(setFont(productTemplate.font));
  }

  if (productTemplate.textTransform) {
    dispatch(setTextTransform(productTemplate.textTransform));
  }

  if (productTemplate.centerText !== undefined && productTemplate.centerText !== null) {
    dispatch(setName(1, productTemplate.centerText));
  }

  if (productTemplate.fontSizes && !query.template) {
    productTemplate.fontSizes.forEach((size, generation) =>
      dispatch(setFontSize(generation, size))
    )
  }

  if (productTemplate.offsets) {
    productTemplate.offsets.forEach((offset, generation) =>
      dispatch(setOffset(generation, offset))
    )
  }

  if (productTemplate.thicknesses && !query.template) {
    productTemplate.thicknesses.forEach((thickness, generation) =>
      dispatch(setThickness(generation, thickness))
    )
  }

  if (productTemplate.cellTypes && !query.template) {
    productTemplate.cellTypes.forEach((cellType, generation) =>
      dispatch(setCellType(generation, cellType))
    )
  }

  const strokeVisibles = productTemplate.strokeVisibles || [];
  range(0, 10).forEach(g => {
    const visible = strokeVisibles[g] === undefined ? true : strokeVisibles[g];
    dispatch(setStrokeVisible(g, visible))
  })

  if (productTemplate.thematic) {
    dispatch(setThematic(productTemplate.thematic));
  }

  const angle = Number.parseInt(query.angle);
  if (angle) {
    dispatch(setAngle(Math.min(360, Math.max(30, angle))));
  } else if (productTemplate.angle && !query.template) {
    dispatch(setAngle(productTemplate.angle));
  }

  if (productTemplate.angleOffset !== undefined) {
    dispatch(setAngleOffset(productTemplate.angleOffset));
  }


  if (state.ui.interfaceMode === 'rootsgifts') {
    dispatch(setMargin(20));
    range(6, 10).forEach(g => dispatch(setCellType(g, 'vertical-curved')));
  } else {
    dispatch(setMargin(200));
  }

  if (query.chartBackground) {
    dispatch(setChartBackground('#' + query.chartBackground));
  } else if (productTemplate.chartBackground) {
    dispatch(setChartBackground(productTemplate.chartBackground));
  } else {
    dispatch(setChartBackground(null));
  }

  dispatch(setGenerationsLocked(!!productTemplate.generationsLocked));

  dispatch(setGenerations(generations));
}


const diaDeLosMuertos = {
  template: 'half-circle',
  palette: 'thematic',
  variation: 'dia-de-los-muertos',
  thematic: 'dia-de-los-muertos',
  font: 'Nunito',
  thicknesses: [90],
  strokeVisibles: [false, false, false, false],
  generationsLocked: false,
  fontSizes: [0, 18],
  offsets: [0, 0],
  angle: 194,
}

const diaDeLosMuertosSkull = {
  template: 'half-circle',
  palette: 'thematic',
  variation: 'dia-de-los-muertos-skull',
  thematic: 'dia-de-los-muertos-skull',
  font: 'Nunito',
  thicknesses: [260],
  strokeVisibles: [false],
  generationsLocked: false,
  fontSizes: [0],
  offsets: [0, 0],
  angle: 194,
}


const productTemplates = {
  'seafoam-ombre-circle-family-tree-chart-1': {
    template: 'circle-horizontal',
    palette: 'ombre',
    variation: 'seafoam',
    centerText: '&',
    fontSizes: [80],
    font: 'Questrial',
    strokeWidth: 0,
    textTransform: 'uppercase',
    flipped: false,
  },
  'rose-ombre-fan-family-tree-chart-2': {
    template: 'fanchart',
    palette: 'ombre',
    variation: 'rose',
    centerText: '',
    font: 'Josefin Sans',
    strokeWidth: 8,
    textTransform: 'uppercase',
    flipped: false,
  },
  'mauve-ombre-circle-family-tree-chart-3': {
    template: 'circle-vertical',
    palette: 'ombre',
    variation: 'mauve',
    fontSizes: [80],
    centerText: '',
    font: 'Montserrat',
    strokeWidth: 0,
    textTransform: 'uppercase',
    flipped: false,
  },
  'dusky-iris-fan-family-tree-chart-4': {
    template: 'fanchart',
    palette: 'doubleDarkChroma',
    variation: 'dusky-iris',
    centerText: '',
    font: 'Montserrat',
    strokeWidth: 0,
    textTransform: 'none',
  },
  'dusky-iris-circle-family-tree-chart-5': {
    template: 'circle-vertical',
    palette: 'doubleDarkChroma',
    variation: 'dusky-iris',
    centerText: '',
    font: 'Nunito',
    strokeWidth: 0,
    textTransform: 'none',
    flipped: true,
  },
  'evening-patriot-circle-family-tree-chart-6': {
    template: 'circle-horizontal',
    palette: 'doubleDarkChroma',
    variation: 'evening-patriot',
    centerText: '',
    font: 'Montserrat',
    strokeWidth: 0,
    textTransform: 'uppercase',
    flipped: false,
  },
  'evening-patriot-circle-family-tree-chart-7': {
    template: 'circle-horizontal',
    palette: 'doubleDarkChroma',
    variation: 'evening-patriot',
    centerText: '&',
    fontSizes: [80],
    offsets: [2],
    font: 'Voltaire',
    strokeWidth: 10,
    textTransform: 'uppercase',
    flipped: true,
  },
  'majestic-tiger-fan-family-tree-chart-8': {
    template: 'fanchart',
    palette: 'doubleDarkChroma',
    variation: 'majestic-tiger',
    centerText: '',
    font: 'Abel',
    strokeWidth: 10,
    textTransform: 'uppercase',
    flipped: false,
  },
  'majestic-tiger-circle-family-tree-chart-9': {
    template: 'circle-vertical',
    palette: 'doubleDarkChroma',
    variation: 'majestic-tiger',
    centerText: '',
    font: 'Voltaire',
    strokeWidth: 5,
    textTransform: 'none',
    flipped: true,
  },
  'sea-charcoal-circle-family-tree-chart-10': {
    template: 'circle-horizontal',
    palette: 'doubleDarkChroma',
    variation: 'sea-charcoal',
    centerText: '',
    fontSizes: [80],
    offsets: [-1],
    font: 'Rasa',
    strokeWidth: 10,
    textTransform: 'uppercase',
    flipped: false,
  },
  'sea-charcoal-fan-family-tree-chart-11': {
    template: 'fanchart',
    palette: 'doubleDarkChroma',
    variation: 'sea-charcoal',
    centerText: '',
    font: 'Amatic SC',
    fontSizes: [31, 26, 24, 23, 20, 16, 15, 13, 13, 13],
    offsets: [0, 0, -8, -9],
    strokeWidth: 4,
    textTransform: 'none',
    flipped: false,
  },
  'mermaid-fan-family-tree-chart-12': {
    template: 'fanchart',
    palette: 'doubleDarkChroma',
    variation: 'mermaid',
    centerText: '&',
    font: 'Voltaire',
    strokeWidth: 4,
    textTransform: 'none',
    flipped: false,
    fontSizes: [20, 20, 16, 20, 16, 12, 13, 11, 11, 11],
    offsets: [2],
  },
  'mother-earth-circle-family-tree-chart-13': {
    template: 'circle-vertical',
    palette: 'doubleDarkChroma',
    variation: 'mother-earth',
    centerText: '',
    font: 'Nunito',
    strokeWidth: 0,
    textTransform: 'none',
    flipped: false,
  },
  'mother-earth-circle-family-tree-chart-14': {
    template: 'circle-horizontal',
    palette: 'doubleDarkChroma',
    variation: 'mother-earth',
    centerText: '',
    font: 'Questrial',
    strokeWidth: 10,
    textTransform: 'uppercase',
    flipped: true,
  },
  'goldenrod-steel-fan-family-tree-chart-15': {
    template: 'fanchart',
    palette: 'creamyRings',
    variation: 'goldenrod-steel',
    centerText: '',
    // centerOffset: 0,
    font: 'Questrial',
    strokeWidth: 0,
    textTransform: 'uppercase',
    flipped: false,
  },
  'goldenrod-steel-circle-family-tree-chart-16': {
    template: 'circle-vertical',
    palette: 'creamyRings',
    variation: 'goldenrod-steel',
    centerText: '',
    font: 'Amatic SC',
    strokeWidth: 0,
    textTransform: 'none',
    flipped: true,
    fontSizes: [80, 40, 40, 38, 34, 30, 28],
  },
  'ripe-plum-circle-family-tree-chart-17': {
    template: 'circle-horizontal',
    palette: 'creamyRings',
    variation: 'ripe-plum',
    centerText: '',
    centerFontSize: 80,
    centerOffset: 0,
    font: 'Nunito',
    strokeWidth: 0,
    textTransform: 'none',
    flipped: false,
  },
  'ripe-plum-fan-family-tree-chart-18': {
    template: 'fanchart',
    palette: 'creamyRings',
    variation: 'ripe-plum',
    centerText: '',
    font: 'Montserrat',
    strokeWidth: 10,
    textTransform: 'none',
    flipped: true,
  },
  'morning-patriot-circle-family-tree-chart-19': {
    template: 'circle-vertical',
    palette: 'creamyRings',
    variation: 'morning-patriot',
    centerText: '',
    font: 'EB Garamond',
    strokeWidth: 0,
    textTransform: 'uppercase',
    flipped: false,
  },
  'morning-patriot-circle-family-tree-chart-20': {
    template: 'circle-horizontal',
    palette: 'creamyRings',
    variation: 'morning-patriot',
    centerText: '&',
    centerFontSize: 80,
    centerOffset: 2,
    font: 'Abel',
    strokeWidth: 0,
    textTransform: 'uppercase',
    flipped: true,
  },
  'beach-day-fan-family-tree-chart-21': {
    template: 'fanchart',
    palette: 'creamyRings',
    variation: 'beach-day',
    centerText: '',
    font: 'Abel',
    strokeWidth: 0,
    textTransform: 'uppercase',
    flipped: false,
    // thicknesses: [0, 138],
    // offsets: [0, 0, 0],
  },
  'beach-day-circle-family-tree-chart-22': {
    template: 'circle-vertical',
    palette: 'creamyRings',
    variation: 'beach-day',
    centerText: '',
    font: 'Montserrat',
    strokeWidth: 0,
    textTransform: 'none',
    flipped: true,
    // fontSizes: [10, 35, 30, 30, 22],
    // offsets: [0, 8, 4, 2, 2],
  },
  'golden-sunset-fan-family-tree-chart-23': {
    template: 'fanchart',
    palette: 'creamyRings',
    variation: 'golden-sunset',
    centerText: '',
    font: 'Montserrat',
    strokeWidth: 0,
    textTransform: 'none',
    flipped: false,
  },
  'golden-sunset-circle-family-tree-chart-24': {
    template: 'circle-horizontal',
    palette: 'creamyRings',
    variation: 'golden-sunset',
    centerText: '',
    font: 'Cinzel',
    strokeWidth: 10,
    textTransform: 'none',
    flipped: true,
  },
  'luck-o-the-irish-circle-family-tree-chart-25': {
    template: 'circle-vertical',
    palette: 'creamyRings',
    variation: 'luck-o-the-irish',
    centerText: '',
    font: 'Amatic SC',
    strokeWidth: 10,
    textTransform: 'uppercase',
    flipped: false,
  },
  'goldenrod-fan-family-tree-chart-26': {
    template: 'fanchart',
    palette: 'creamyRings',
    variation: 'goldenrod',
    centerText: '',
    font: 'Josefin Sans',
    strokeWidth: 1,
    textTransform: 'uppercase',
    flipped: false,
  },
  'goldenrod-circle-family-tree-chart-27': {
    template: 'circle-horizontal',
    palette: 'creamyRings',
    variation: 'goldenrod',
    centerText: '',
    font: 'Questrial',
    strokeWidth: 10,
    textTransform: 'none',
    flipped: true,
  },
  'tea-party-circle-family-tree-chart-28': {
    template: 'circle-vertical',
    palette: 'creamyRings',
    variation: 'teaparty',
    centerText: '',
    font: 'Ovo',
    strokeWidth: 0,
    textTransform: 'none',
    flipped: false,
    fontSizes: [10, 55, 45, 35, 35],
    offsets: [0, 7, -1, 2, -4],
  },
  'tea-party-fan-family-tree-chart-29': {
    template: 'fanchart',
    palette: 'creamyRings',
    variation: 'teaparty',
    centerText: '',
    font: 'Ovo',
    strokeWidth: 10,
    textTransform: 'uppercase',
    flipped: true,
  },
  'spring-eggplant-circle-family-tree-chart-30': {
    template: 'circle-vertical',
    palette: 'creamyRings',
    variation: 'spring-eggplant',
    centerText: '',
    font: 'Voltaire',
    strokeWidth: 0,
    textTransform: 'uppercase',
    flipped: false,
  },
  'heirloom-tomato-circle-family-tree-chart-31': {
    template: 'circle-horizontal',
    palette: 'creamyRings',
    variation: 'heirloom-tomato',
    centerText: '&',
    centerFontSize: 80,
    centerOffset: 1,
    font: 'Nunito',
    strokeWidth: 0,
    textTransform: 'uppercase',
    flipped: false,
  },
  'heirloom-tomato-fan-family-tree-chart-32': {
    template: 'fanchart',
    palette: 'creamyRings',
    variation: 'heirloom-tomato',
    centerText: '',
    fontSizes: [24, 20, 16, 18, 13, 15, 11],
    offsets: [0, 0, 0, 0, 0, 0],
    font: 'Montserrat',
    strokeWidth: 10,
    textTransform: 'uppercase',
    flipped: true,
  },
  'violet-clementine-circle-family-tree-chart-33': {
    template: 'circle-vertical',
    palette: 'creamyRings',
    variation: 'violet-clementine',
    centerText: '',
    font: 'Nunito',
    strokeWidth: 0,
    textTransform: 'uppercase',
    flipped: false,
  },
  'turquoise-ebony-circle-family-tree-chart-34': {
    template: 'circle-horizontal',
    palette: 'creamyRings',
    variation: 'turquoise-ebony',
    centerText: '',
    font: 'Abel',
    strokeWidth: 0,
    textTransform: 'uppercase',
    flipped: false,
  },
  'turquoise-ebony-fan-family-tree-chart-35': {
    template: 'fanchart',
    palette: 'creamyRings',
    variation: 'turquoise-ebony',
    centerText: '',
    font: 'Abel',
    strokeWidth: 10,
    textTransform: 'uppercase',
    flipped: true,
  },
  'aquamarine-circle-family-tree-chart-36': {
    template: 'circle-vertical',
    palette: 'colorRings',
    variation: 'aquamarine',
    centerText: '',
    font: 'Josefin Sans',
    strokeWidth: 5,
    textTransform: 'none',
    flipped: false,
  },
  'amathest-circle-family-tree-chart-37': {
    template: 'circle-horizontal',
    palette: 'colorRings',
    variation: 'amathest',
    centerText: '',
    font: 'Cinzel',
    strokeWidth: 10,
    textTransform: 'none',
    flipped: false,
  },
  'rose-quartz-fan-family-tree-chart-38': {
    template: 'fanchart',
    palette: 'colorRings',
    variation: 'rose-quartz',
    centerText: '',
    font: 'Ovo',
    strokeWidth: 2,
    textTransform: 'uppercase',
    flipped: false,
  },
  'frosted-diamond-circle-family-tree-chart-39': {
    template: 'circle-vertical',
    palette: 'frosted',
    variation: 'frosted-diamond',
    font: 'Nunito',
    strokeWidth: 0,
    textTransform: 'uppercase',
    flipped: false,
  },
  'frosted-rose-circle-family-tree-chart-40': {
    template: 'circle-horizontal',
    palette: 'frosted',
    variation: 'frosted-rose',
    centerText: '&',
    fontSizes: [60],
    offsets: [2],
    font: 'Questrial',
    strokeWidth: 0,
    textTransform: 'uppercase',
    flipped: false,
  },
  'frosted-aquamarine-fan-family-tree-chart-41': {
    template: 'fanchart',
    palette: 'frosted',
    variation: 'frosted-aquamarine',
    centerText: '',
    font: 'Montserrat',
    strokeWidth: 0,
    textTransform: 'uppercase',
    flipped: false,
  },
  'frosted-opal-circle-family-tree-chart-42': {
    template: 'circle-vertical',
    palette: 'frosted',
    variation: 'frosted-opal',
    centerText: '',
    font: 'Rasa',
    strokeWidth: 0,
    textTransform: 'uppercase',
    flipped: false,
  },
  'classic-white-circle-family-tree-chart-43': {
    template: 'circle-horizontal',
    palette: 'solidColor',
    variation: 'white',
    centerText: '',
    fontSizes: [80],
    offsets: [0],
    font: 'Nunito',
    strokeWidth: 5,
    textTransform: 'uppercase',
    flipped: false,
  },
  'white-simplicity-circle-family-tree-chart-44': {
    template: 'circle-horizontal',
    palette: 'solidColor',
    variation: 'white',
    centerText: '',
    font: 'Josefin Sans',
    strokeWidth: 0,
    textTransform: 'uppercase',
    flipped: false,
  },
  'classic-white-fan-family-tree-chart-45': {
    template: 'fanchart',
    palette: 'solidColor',
    variation: 'white',
    centerText: '',
    font: 'EB Garamond',
    strokeWidth: 2,
    textTransform: 'uppercase',
    flipped: false,
  },
  'white-simplicity-fan-family-tree-chart-46': {
    template: 'fanchart',
    palette: 'solidColor',
    variation: 'white',
    centerText: '',
    font: 'Montserrat',
    strokeWidth: 0,
    textTransform: 'uppercase',
    flipped: false,
  },
  'concrete-circle-family-tree-chart-47': {
    template: 'circle-horizontal',
    palette: 'solidColor',
    variation: 'concrete',
    centerText: '',
    font: 'Montserrat',
    strokeWidth: 9,
    textTransform: 'uppercase',
    flipped: false,
  },
  'simply-concrete-fan-family-tree-chart-48': {
    template: 'fanchart',
    palette: 'solidColor',
    variation: 'concrete',
    centerText: '',
    font: 'Voltaire',
    strokeWidth: 0,
    textTransform: 'uppercase',
    flipped: false,
  },
  'clean-blackboard-fan-family-tree-chart-49': {
    template: 'fanchart',
    palette: 'solidColor',
    variation: 'blackboard',
    centerText: '',
    font: 'Voltaire',
    strokeWidth: 0,
    textTransform: 'uppercase',
    flipped: false,
  },
  'clean-blackboard-circle-family-tree-chart-50': {
    template: 'circle-horizontal',
    palette: 'solidColor',
    variation: 'blackboard',
    centerText: '&',
    fontSizes: [80],
    offsets: [0],
    font: 'Josefin Sans',
    strokeWidth: 10,
    textTransform: 'uppercase',
    flipped: false,
  },
  'chocolate-strawberry-circle-family-tree-chart-51': {
    template: 'circle-horizontal',
    palette: 'bananaSundaes',
    variation: 'chocolate-strawberry',
    centerText: '&',
    fontSizes: [80],
    offsets: [0],
    font: 'Josefin Sans',
    strokeWidth: 10,
    textTransform: 'uppercase',
    flipped: false,
  },
  'chocolate-strawberry-fan-family-tree-chart-52': {
    template: 'fanchart',
    palette: 'bananaSundaes',
    variation: 'chocolate-strawberry',
    centerText: '',
    font: 'Nunito',
    strokeWidth: 10,
    textTransform: 'uppercase',
    flipped: true,
  },
  'mocha-delight-fan-family-tree-chart-53': {
    template: 'fanchart',
    palette: 'bananaSundaes',
    variation: 'mocha-chip',
    centerText: '',
    font: 'Montserrat',
    strokeWidth: 5,
    textTransform: 'uppercase',
    flipped: false,
  },
  'chocolate-chip-circle-family-tree-chart-54': {
    template: 'circle-vertical',
    palette: 'bananaSundaes',
    variation: 'chocolate-chip',
    centerText: '',
    font: 'Nunito',
    strokeWidth: 0,
    textTransform: 'uppercase',
    flipped: false,
  },
  'carmel-vanilla-circle-family-tree-chart-55': {
    template: 'circle-horizontal',
    palette: 'bananaSundaes',
    variation: 'caramel-vanilla',
    centerText: '',
    font: 'Amatic SC',
    strokeWidth: 0,
    textTransform: 'uppercase',
    flipped: false,
    fontSizes: [10, 44, 36, 32, 22],
    offsets: [0, 20, 0, 3, 2],
  },
  'whirligig-watermelon-fan-family-tree-chart-56': {
    template: 'fanchart',
    palette: 'fiesta',
    variation: 'whirligig-watermelon',
    centerText: '',
    font: 'Nunito',
    strokeWidth: 2,
    textTransform: 'uppercase',
    flipped: true,
  },
  'whirligig-watermelon-circle-family-tree-chart-57': {
    template: 'circle-vertical',
    palette: 'fiesta',
    variation: 'whirligig-watermelon',
    centerText: '',
    font: 'Abel',
    strokeWidth: 0,
    textTransform: 'uppercase',
    flipped: true,
  },
  'whirligig-watermelon-circle-family-tree-chart-58': {
    template: 'circle-horizontal',
    palette: 'fiesta',
    variation: 'whirligig-watermelon',
    centerText: '',
    font: 'Josefin Sans',
    strokeWidth: 10,
    textTransform: 'none',
    flipped: false,
  },
  'raspberry-revelry-fan-family-tree-chart-59': {
    template: 'fanchart',
    palette: 'fiesta',
    variation: 'raspberry-revelry',
    centerText: '',
    font: 'Montserrat',
    strokeWidth: 5,
    textTransform: 'none',
    flipped: false,
  },
  'raspberry-revelry-circle-family-tree-chart-60': {
    template: 'circle-vertical',
    palette: 'fiesta',
    variation: 'raspberry-revelry',
    centerText: '',
    font: 'Nunito',
    strokeWidth: 0,
    textTransform: 'none',
    flipped: true,
  },
  'merry-melon-circle-family-tree-chart-61': {
    template: 'circle-horizontal',
    palette: 'fiesta',
    variation: 'merry-melon',
    centerText: '',
    font: 'Voltaire',
    strokeWidth: 10,
    textTransform: 'none',
    flipped: false,
  },
  'merry-melon-fan-family-tree-chart-62': {
    template: 'fanchart',
    palette: 'fiesta',
    variation: 'merry-melon',
    centerText: '',
    font: 'Voltaire',
    strokeWidth: 5,
    textTransform: 'uppercase',
    flipped: true,
  },
  'grape-gala-circle-family-tree-chart-63': {
    template: 'circle-vertical',
    palette: 'fiesta',
    variation: 'grape-gala',
    centerText: '',
    font: 'Nunito',
    strokeWidth: 5,
    textTransform: 'uppercase',
    flipped: false,
  },
  'grape-gala-fan-family-tree-chart-64': {
    template: 'fanchart',
    palette: 'fiesta',
    variation: 'grape-gala',
    centerText: '',
    font: 'Abel',
    strokeWidth: 2,
    textTransform: 'uppercase',
    flipped: true,
  },
  'natural-stem-circle-family-tree-chart-65': {
    template: 'circle-vertical',
    palette: 'naturalLight',
    variation: 'natural-stem',
    centerText: '',
    font: 'Montserrat',
    strokeWidth: 0,
    textTransform: 'uppercase',
    flipped: false,
  },
  'natural-leaf-circle-family-tree-chart-66': {
    template: 'circle-horizontal',
    palette: 'naturalLight',
    variation: 'natural-leaf',
    centerText: '&',
    fontSizes: [80],
    offsets: [0],
    font: 'Nunito',
    strokeWidth: 5,
    textTransform: 'uppercase',
    flipped: false,
  },
  'natural-petal-fan-family-tree-chart-67': {
    template: 'fanchart',
    palette: 'naturalLight',
    variation: 'natural-petal',
    centerText: '',
    font: 'Karma',
    strokeWidth: 10,
    textTransform: 'uppercase',
    flipped: false,
  },
  'golden-bands': {
    template: 'circle-vertical',
    palette: 'banded',
    variation: 'gold',
    font: 'Abel',
    textTransform: 'uppercase',
  },
  'golden-bands-1': {
    template: 'fanchart',
    palette: 'banded',
    variation: 'gold',
    font: 'Abel',
    textTransform: 'uppercase',
  },
  'banded-rainbow': {
    template: 'fanchart',
    palette: 'banded',
    variation: 'rainbow',
    font: 'Nunito',
    // thicknesses: [130, 120, 120, 120, 120],
  },
  'bb8-family-tree-chart-68': {
    template: 'circle-vertical',
    palette: 'thematic',
    variation: 'bb8',
    thematic: 'bb8',
    font: 'Montserrat',
    textTransform: 'uppercase',
    thicknesses: [130, 120, 120, 120, 120],
  },
  'millenium-falcon-family-tree-chart-69': {
    template: 'circle-vertical',
    palette: 'thematic',
    variation: 'millenium-falcon',
    thematic: 'millenium-falcon',
    font: 'Montserrat',
    textTransform: 'uppercase',
    thicknesses: [130, 120, 120, 120, 120],
  },
  'captain-america-family-tree-chart-71': {
    template: 'circle-vertical',
    palette: 'thematic',
    variation: 'captain-america',
    thematic: 'captain-america',
    font: 'Montserrat',
    textTransform: 'uppercase',
    thicknesses: [230, 120, 120, 130, 140, 150, 150, 160, 150, 140],
    fontSizes: [22],
    offsets: [-20],
  },
  'uss-enterprise-family-tree-chart-70': {
    template: 'circle-vertical',
    palette: 'thematic',
    variation: 'uss-enterprise',
    thematic: 'uss-enterprise',
    font: 'Montserrat',
    textTransform: 'uppercase',
    thicknesses: [130, 120, 120, 120, 120],
  },
  'kids-minimalist-animal-turtle-family-tree-chart-74': {
    template: 'half-circle',
    palette: 'thematic',
    variation: 'minimalist-turtle',
    thematic: 'minimalist-turtle',
    font: 'Nunito',
    textTransform: 'none',
    generations: 4,
    generationsLocked: true,
    centerText: '',
  },
  'kids-minimalist-animal-toucan-family-tree-chart-75': {
    template: 'half-circle',
    palette: 'thematic',
    variation: 'minimalist-toucan',
    thematic: 'minimalist-toucan',
    font: 'Nunito',
    textTransform: 'uppercase',
    generations: 4,
    generationsLocked: true,
    centerText: '',
  },
  'kids-minimalist-animal-whale-blue-family-tree-chart-73': {
    template: 'half-circle-inverted',
    palette: 'thematic',
    variation: 'minimalist-whale',
    thematic: 'minimalist-whale',
    font: 'Nunito',
    textTransform: 'none',
    generations: 4,
    generationsLocked: true,
    centerText: '',
  },
  'kids-minimalist-animal-ladybug-family-tree-chart-78': {
    template: 'half-circle',
    palette: 'thematic',
    variation: 'minimalist-lady-bug',
    thematic: 'minimalist-lady-bug',
    font: 'Nunito',
    textTransform: 'none',
    // thicknesses: [0, 140, 120],
    fontSizes: [17, 17, 16, 16, 15, 14],
    angle: 300,
    generations: 4,
    generationsLocked: true,
  },
  'kids-minimalist-animal-crab-family-tree-chart-72': {
    template: 'half-circle',
    palette: 'thematic',
    variation: 'minimalist-crab',
    thematic: 'minimalist-crab',
    font: 'Nunito',
    textTransform: 'none',
    generations: 4,
    generationsLocked: true,
    centerText: '',
  },
  'kids-minimalist-animal-fox-family-tree-chart-76': {
    template: 'circle-horizontal',
    palette: 'thematic',
    variation: 'minimalist-fox',
    thematic: 'minimalist-fox',
    font: 'Nunito',
    textTransform: 'none',
    thicknesses: [0, 116, 100, 100, 140],
    generations: 5,
    generationsLocked: true,
    centerText: '',
  },
  'kids-minimalist-animal-pig-family-tree-chart-77': {
    template: 'circle-horizontal',
    palette: 'thematic',
    variation: 'minimalist-pig',
    thematic: 'minimalist-pig',
    font: 'Nunito',
    textTransform: 'none',
    generations: 5,
    generationsLocked: true,
    centerText: '',
  },
  'safari-giraffe': {
    template: 'half-circle-inverted',
    palette: 'thematic',
    variation: 'safari-giraffe',
    thematic: 'safari-giraffe',
    font: 'Nunito',
    textTransform: 'none',
    generations: 4,
    generationsLocked: true,
    centerText: '',
  },
  'safari-snake': {
    template: 'half-circle-inverted',
    palette: 'thematic',
    variation: 'minimalist-turtle',
    thematic: 'safari-giraffe',
    font: 'Nunito',
    textTransform: 'none',
    generations: 4,
    generationsLocked: true,
    centerText: '',
  },
  'safari-hippo': {
    template: 'half-circle',
    palette: 'thematic',
    variation: 'safari-hippo',
    thematic: 'safari-hippo',
    font: 'Nunito',
    textTransform: 'none',
    generations: 4,
    generationsLocked: true,
    centerText: '',
  },
  'pig': {
    template: 'half-circle',
    palette: 'thematic',
    variation: 'pig',
    thematic: 'pig',
    font: 'Nunito',
    textTransform: 'none',
    generations: 4,
    generationsLocked: true,
    centerText: '',
  },
  'peacock': {
    template: 'half-circle-inverted',
    palette: 'thematic',
    variation: 'peacock',
    thematic: 'peacock',
    font: 'Nunito',
    textTransform: 'none',
    strokeVisibles: [false],
    thicknesses: [90],
    angle: 192,
    generations: 6,
    generationsLocked: false,
    centerText: '',
  },
  'watermelon': {
    template: 'half-circle',
    palette: 'thematic',
    variation: 'watermelon',
    font: 'Nunito',
    textTransform: 'none',
    generations: 4,
    generationsLocked: true,
    centerText: '',
  },
  'sunrise': {
    template: 'half-circle',
    palette: 'thematic',
    variation: 'sunrise',
    thematic: 'sunrise',
    font: 'Nunito',
    textTransform: 'none',
    generations: 4,
    generationsLocked: true,
    centerText: '',
  },
  'panda': {
    template: 'half-circle',
    palette: 'thematic',
    variation: 'panda',
    thematic: 'panda',
    font: 'Nunito',
    textTransform: 'none',
    generations: 4,
    generationsLocked: true,
    centerText: '',
  },
  'sunflower': {
    template: 'circle-horizontal',
    palette: 'thematic',
    variation: 'sunflower',
    thematic: 'sunflower',
    font: 'Nunito',
    textTransform: 'none',
    generations: 4,
    generationsLocked: true,
    centerText: '',
  },
  'unicorn': {
    template: 'half-circle',
    palette: 'thematic',
    variation: 'unicorn',
    thematic: 'unicorn',
    font: 'Nunito',
    textTransform: 'none',
    generations: 4,
    generationsLocked: false,
    centerText: '',
  },
  'pirate-ship': {
    template: 'half-circle-inverted',
    palette: 'thematic',
    variation: 'viking-ship',
    thematic: 'pirate-ship',
    font: 'Nunito',
    textTransform: 'none',
    thicknesses: [90],
    generations: 4,
    generationsLocked: false,
    fontSizes: [80, 18],
    offsets: [0, 0],
    centerText: 'M',
  },
  'viking-ship': {
    template: 'half-circle-inverted',
    palette: 'thematic',
    variation: 'viking-ship',
    thematic: 'viking-ship',
    font: 'Nunito',
    textTransform: 'none',
    thicknesses: [90],
    generations: 4,
    generationsLocked: false,
    fontSizes: [80, 18],
    offsets: [0, 0],
    centerText: 'M',
  },
  'dia-de-los-muertos-integrated-fiesta-sugar-skull-family-tree-chart': {
    ...diaDeLosMuertos,
  },
  'dia-de-los-muertos-integrated-rose-sugar-skull-family-tree-chart': {
    ...diaDeLosMuertos,
    variation: 'dia-de-los-muertos-dark',
  },
  'dia-de-los-muertos-sugar-skull-family-tree-chart': {
    ...diaDeLosMuertos,
    variation: 'dia-de-los-muertos-raspberry',
  },
  'dia-de-los-muertos-blue-gold-sugar-skull-family-tree-chart': {
    ...diaDeLosMuertos,
    variation: 'dia-de-los-muertos-fall',
  },
  'dia-de-los-muertos-fiesta-sugar-skull-family-tree-chart': {
    ...diaDeLosMuertosSkull,
  },
  'dia-de-los-muertos-rose-sugar-skull-family-tree-chart': {
    ...diaDeLosMuertosSkull,
    variation: 'dia-de-los-muertos-skull-dark',
  },
  'dia-de-los-muertos-raspberry-sugar-skull-family-tree-chart': {
    ...diaDeLosMuertosSkull,
    variation: 'dia-de-los-muertos-skull-raspberry',
  },
  'dia-de-los-muertos-fall-sugar-skull-family-tree-chart': {
    ...diaDeLosMuertosSkull,
    variation: 'dia-de-los-muertos-skull-fall',
  },
  'dog': {
    template: 'half-circle',
    palette: 'thematic',
    variation: 'dog',
    thematic: 'dog',
    font: 'Nunito',
    textTransform: 'none',
    generations: 4,
    generationsLocked: false,
    centerText: '',
  },
  'dinosaur': {
    template: 'half-circle',
    palette: 'thematic',
    variation: 'dinosaur',
    thematic: 'dinosaur',
    font: 'Nunito',
    textTransform: 'none',
    generations: 4,
    generationsLocked: false,
    centerText: '',
  },
  'rainbow': {
    template: 'half-circle',
    palette: 'thematic',
    variation: 'rainbow',
    font: 'Nunito',
    textTransform: 'none',
    generations: 7,
    thicknesses: [180],
    angle: 192,
    generationsLocked: true,
    centerText: '',
    fontSizes: [22, 22, 20],
    offsets: [0, 0],
    cellTypes: ['horizontal-centered', 'horizontal-centered', 'horizontal-centered']
  },

  'family-search': {
    template: 'fanchart',
    palette: 'thematic',
    variation: 'family-search',
    font: 'Montserrat',
    textTransform: 'none',
    angle: 216,
  },
  'tree-roots': {
    template: 'fanchart',
    palette: 'thematic',
    variation: 'tree-roots',
    thematic: 'tree-roots',
    font: 'Montserrat',
    textTransform: 'none',
    angle: 206,
    thicknesses: [90],
  },
  'white-transparent': {
    template: 'fanchart',
    palette: 'solidColor',
    variation: 'white-transparent',
    font: 'Montserrat',
    textTransform: 'none',
  },
  'black-transparent': {
    template: 'fanchart',
    palette: 'solidColor',
    variation: 'black-transparent',
    font: 'Montserrat',
    textTransform: 'none',
  }

}


// WEBPACK FOOTER //
// ./src/core/templates/product.js